import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, ClipboardCheck, RefreshCcw, X, CheckCircle2, PlayCircle } from "lucide-react";

const NEXT_STATUS = {
  lab: [
    { key: "confirmed", label: "Confirm" },
    { key: "sample_collected", label: "Sample collected" },
    { key: "report_ready", label: "Report ready" },
    { key: "completed", label: "Mark complete" },
    { key: "rejected", label: "Reject" },
  ],
  pharmacy: [
    { key: "confirmed", label: "Accept order" },
    { key: "in_progress", label: "Out for delivery" },
    { key: "completed", label: "Delivered" },
    { key: "rejected", label: "Reject" },
  ],
  hospital: [
    { key: "confirmed", label: "Confirm bed" },
    { key: "completed", label: "Patient admitted" },
    { key: "rejected", label: "Reject" },
  ],
  doctor: [
    { key: "confirmed", label: "Confirm appt" },
    { key: "completed", label: "Complete" },
    { key: "rejected", label: "Reject" },
  ],
};

const STATUS_COLOR = {
  confirmed: "bg-teal-100 text-teal-800",
  in_progress: "bg-amber-100 text-amber-900",
  sample_collected: "bg-cyan-100 text-cyan-800",
  report_ready: "bg-cyan-100 text-cyan-800",
  completed: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
  scheduled: "bg-slate-100 text-slate-800",
};

export default function PartnerPortal() {
  const { user } = useAuth();
  const [state, setState] = useState({ kind: "", bookings: [] });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/partner/summary");
      setState(data);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/partner/bookings/${id}/status`, { status });
      toast.success(`Updated → ${status}`);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const label = state.kind ? state.kind.replace(/^./, (c) => c.toUpperCase()) : "";
  const options = NEXT_STATUS[state.kind] || [];

  const renderRow = (b) => {
    if (state.kind === "lab")     return `${b.tests?.join(", ")} · ${b.mode?.replace("_", " ")} · ${new Date(b.slot_time).toLocaleString()}`;
    if (state.kind === "pharmacy") return `${b.medicines?.length || 0} items · ${b.address}`;
    if (state.kind === "hospital") return `${b.bed_type?.toUpperCase()} bed · ${b.patient_name}`;
    if (state.kind === "doctor")   return `${b.mode?.replace("_", " ")} · ${new Date(b.slot_time).toLocaleString()}`;
    return "";
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pb-24 sm:pb-8">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-teal-700">Partner portal · {label}</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold mt-1">Bookings for {user?.name}</h1>
          <p className="text-slate-600 text-sm">Confirm, progress, or complete requests as they come in.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="partner-refresh"><RefreshCcw className="w-4 h-4 mr-2" /> Refresh</Button>
      </div>

      {state.bookings.length === 0 ? (
        <Card className="p-10 rounded-2xl text-center text-slate-500" data-testid="no-bookings">
          <Building2 className="w-8 h-8 mx-auto text-slate-300 mb-3" />
          {loading ? "Loading…" : "No bookings yet — new requests will show up here in real time."}
        </Card>
      ) : (
        <div className="space-y-3">
          {state.bookings.map((b) => (
            <Card key={b.id} className="p-4 sm:p-5 rounded-2xl card-lift" data-testid={`booking-${b.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <ClipboardCheck className="w-4 h-4 text-teal-700" />
                    <div className="font-display font-bold">{b.patient_name || b.contact || "Booking"}</div>
                    <Badge className={`${STATUS_COLOR[b.status] || "bg-slate-100 text-slate-800"} border-0 text-[10px] uppercase tracking-widest`}>{b.status}</Badge>
                  </div>
                  <div className="text-xs text-slate-600 mt-1">{renderRow(b)}</div>
                  {b.amount != null && <div className="text-xs text-slate-500 mt-1 font-mono">₹{b.amount}</div>}
                </div>
                <div className="flex flex-wrap gap-2">
                  {options.map((o) => (
                    <Button key={o.key} size="sm" variant={o.key === "rejected" ? "outline" : "default"}
                      className={o.key === "rejected" ? "text-red-700 border-red-200" : "bg-teal-600 hover:bg-teal-500 text-white"}
                      onClick={() => setStatus(b.id, o.key)}
                      data-testid={`act-${o.key}-${b.id}`}
                    >
                      {o.key === "rejected" ? <X className="w-4 h-4 mr-1" /> : o.key === "completed" ? <CheckCircle2 className="w-4 h-4 mr-1" /> : <PlayCircle className="w-4 h-4 mr-1" />}
                      {o.label}
                    </Button>
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
